import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEPARTMENT_DEFS,
  DEPARTMENT_MAP,
  EMPLOYEE_STATUSES,
  EMPLOYEE_STATUS_MAP,
  WORK_MODES,
  WORK_MODE_MAP,
  type EmployeeStatus,
  type WorkMode,
} from '@goyone/shared';
import { api, qs } from '../../lib/api';
import { useDebounced, useListState } from '../../lib/hooks';
import { date, money } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  Pager,
  SearchBox,
  Stat,
  TableSkeleton,
} from '../../components/ui';

/** ما تُملأ به قوائمُ الاختيار في ملفّ الموظّف. */
interface UnitOption {
  id: string;
  name: string;
  path: string;
}

interface CenterOption {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
}

interface PlanOption {
  id: string;
  name: string;
  startTime: string;
  endTime: string;
  isActive: boolean;
}

interface PositionOption {
  id: string;
  title: string;
  department: string | null;
}

interface GradeOption {
  id: string;
  name: string;
}

/**
 * قراءةُ مفتاحٍ من صفٍّ قد لا يحمله.
 *
 * صفُّ القائمة أخفُّ من صفّ الملفّ — لا يحمل كلَّ المفاتيح — والنافذةُ
 * تُفتح من القائمة ومن الملفّ معًا. فتُقرأ المفاتيحُ بحذرٍ بدل أن يُوسَّع
 * صفُّ القائمة بحقولٍ لا تُعرض فيها.
 */
function pick(row: unknown, key: string): string {
  const value = (row as Record<string, unknown> | undefined)?.[key];
  return typeof value === 'string' ? value : '';
}

interface EmployeeRow {
  id: string;
  code: string | null;
  name: string;
  jobTitle: string | null;
  department: string | null;
  deptKey: string | null;
  email: string | null;
  phone: string | null;
  hireDate: string;
  status: EmployeeStatus;
  baseSalary: string;
  isSalesRep: boolean;
  annualLeaveBalance: string;
}

interface EmployeePage {
  items: EmployeeRow[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/**
 * ملفّاتُ المرشّحين للتعيين.
 *
 * البابُ نفسُه بمرشِّحٍ مثبَّت. ولا يكفي أن يُترك للمستخدم أن يختار الحالةَ
 * من القائمة: من فتح عرضًا وقُبل يسأل «أين ملفُّه الآن؟» ويحتاج جوابًا في
 * القائمة الجانبيّة لا في مرشِّحٍ يتذكّره.
 */
export function PendingEmployees() {
  return <EmployeesList fixedStatus="PENDING" />;
}

export function EmployeesList({
  fixedStatus,
}: {
  /** حالةٌ مثبَّتةٌ لا تُغيَّر من الشاشة — تُستعمل للأبواب المتخصّصة. */
  fixedStatus?: EmployeeStatus;
} = {}) {
  const { atLeast } = useAuth();
  const { state, patch, page } = useListState();
  const [search, setSearch] = useState(state.q ?? '');
  const debounced = useDebounced(search);
  const [creating, setCreating] = useState(false);

  const canEdit = atLeast('ADMIN');
  const query = {
    ...state,
    ...(fixedStatus ? { status: fixedStatus } : {}),
    q: debounced || undefined,
    page,
  };

  const list = useQuery({
    queryKey: ['employees', query],
    queryFn: () => api<EmployeePage>(`/hr/employees${qs(query)}`),
  });

  const departments = useQuery({
    queryKey: ['employees', 'departments'],
    queryFn: () => api<string[]>('/hr/employees/departments'),
  });

  const rows = list.data?.items ?? [];
  const active = rows.filter((e) => e.status === 'ACTIVE').length;
  const payroll = rows
    .filter((e) => !EMPLOYEE_STATUS_MAP[e.status].inactive)
    .reduce((s, e) => s + Number(e.baseSalary), 0);

  return (
    <div className="page">
      <PageHead
        title={
          fixedStatus === 'PENDING'
            ? 'ملفات المرشّحين للتعيين'
            : 'ملفات الموظفين'
        }
        subtitle={
          fixedStatus === 'PENDING'
            ? 'فُتحت ملفّاتُهم بقبول العرض، ولا يصيرون على رأس العمل حتى يُعلَّم عقدُ كلٍّ موقَّعًا'
            : list.data
              ? `${list.data.total} موظفًا`
              : undefined
        }
        actions={
          canEdit && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              موظف جديد
            </button>
          )
        }
      />

      <div className="stats">
        <Stat label="على رأس العمل" value={active} />
        <Stat
          label="أساسي الرواتب في هذه الصفحة"
          value={<span className="num">{money(payroll)}</span>}
        />
      </div>

      <div className="toolbar">
        <SearchBox
          value={search}
          onChange={(v) => {
            setSearch(v);
            patch({ q: v || undefined });
          }}
          placeholder="بحث بالاسم أو الكود أو المسمّى"
        />

        {/* البابُ المتخصّصُ لا يُغيَّر مرشِّحُه من داخله: اسمُ الشاشة يَعِد بما تعرضه. */}
        {!fixedStatus && (
          <select
            className="select"
            value={state.status ?? ''}
            onChange={(e) => patch({ status: e.target.value || undefined })}
          >
            <option value="">كل الحالات</option>
            {EMPLOYEE_STATUSES.map((s) => (
              <option key={s} value={s}>
                {EMPLOYEE_STATUS_MAP[s].label}
              </option>
            ))}
          </select>
        )}

        <select
          className="select"
          value={state.department ?? ''}
          onChange={(e) => patch({ department: e.target.value || undefined })}
        >
          <option value="">كل الأقسام</option>
          {departments.data?.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
        </select>
      </div>

      {list.isError && <ErrorState error={list.error} onRetry={() => list.refetch()} />}

      <div className="card">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>الموظف</th>
                <th>القسم</th>
                <th>تاريخ التعيين</th>
                <th>الأساسي</th>
                <th>رصيد الإجازة</th>
                <th>الحالة</th>
              </tr>
            </thead>
            {list.isLoading && <TableSkeleton rows={6} cols={6} />}

            <tbody>
              {rows.map((e) => (
                <tr key={e.id}>
                  <td>
                    <Link to={`/hr/employees/${e.id}`} className="cell-strong link">
                      {e.name}
                    </Link>
                    <div className="tiny mute">
                      {e.code && <span className="num">{e.code}</span>}
                      {e.code && e.jobTitle && ' — '}
                      {e.jobTitle}
                    </div>
                  </td>
                  <td className="cell-mute">{e.department ?? '—'}</td>
                  <td className="num cell-mute">{date(e.hireDate)}</td>
                  <td className="num">{money(e.baseSalary)}</td>
                  <td className="num cell-mute">{Number(e.annualLeaveBalance)} يوم</td>
                  <td>
                    <Badge kind={EMPLOYEE_STATUS_MAP[e.status].kind}>
                      {EMPLOYEE_STATUS_MAP[e.status].label}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {!list.isLoading && rows.length === 0 && (
          <EmptyState
            title="لا موظفين بعد"
            text="أضف ملفات موظفيك ليعمل الحضور والإجازات والمرتبات."
          />
        )}

        {list.data && <Pager
            page={page}
            pageCount={list.data.pageCount}
            total={list.data.total}
            pageSize={list.data.pageSize}
            onPage={(p) => patch({ page: String(p) })}
          />}
      </div>

      {creating && <EmployeeDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

/**
 * ملف موظف جديد.
 *
 * الاسم وتاريخ التعيين وحدهما مطلوبان: بقية البيانات تُستكمل مع الوقت،
 * وفرضها كلها دفعة واحدة يجعل إدخال عشرين موظفًا عملًا يُؤجَّل.
 */
export function EmployeeDialog({
  employee,
  onClose,
}: {
  employee?: Partial<EmployeeRow> & { id?: string };
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const editing = Boolean(employee?.id);

  const [form, setForm] = useState({
    name: employee?.name ?? '',
    code: employee?.code ?? '',
    jobTitle: employee?.jobTitle ?? '',
    deptKey: (employee as { deptKey?: string | null } | undefined)?.deptKey ?? '',
    email: employee?.email ?? '',
    phone: employee?.phone ?? '',
    hireDate: employee?.hireDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
    baseSalary: employee?.baseSalary ? String(Number(employee.baseSalary)) : '',
    status: employee?.status ?? 'ACTIVE',
    annualLeaveBalance: employee?.annualLeaveBalance
      ? String(Number(employee.annualLeaveBalance))
      : '21',
    insured: Boolean((employee as { insured?: boolean } | undefined)?.insured),
    insuranceNumber:
      (employee as { insuranceNumber?: string | null } | undefined)
        ?.insuranceNumber ?? '',
    insuranceWage: (employee as { insuranceWage?: unknown } | undefined)
      ?.insuranceWage
      ? String(
          Number((employee as { insuranceWage?: unknown }).insuranceWage),
        )
      : '',
    taxable: Boolean((employee as { taxable?: boolean } | undefined)?.taxable),

    /*
     * ما يُختار من إعداد المؤسسة ومن التطوير التنظيميّ.
     *
     * كتابةُ هذه بخطّ اليد كانت تعني «قسم التصميم» و«التصميم» و«الديزاين»
     * في ثلاثة ملفّات — فلا تُجمع كلفةُ وحدةٍ ولا يُقاس حضورٌ على ورديّة.
     * وهي مدخلاتٌ في المرتّب والحضور لا تصنيفاتُ عرض.
     */
    orgUnitId: pick(employee, 'orgUnitId'),
    costCenterId: pick(employee, 'costCenterId'),
    scheduleId: pick(employee, 'scheduleId'),
    positionId: pick(employee, 'positionId'),
    gradeId: pick(employee, 'gradeId'),
    workMode:
      (employee as { workMode?: string | null } | undefined)?.workMode ??
      'ONSITE',
    managerId: pick(employee, 'managerId'),
  });

  /*
   * القوائمُ تُجلب مرّةً وتُشارك بين كلّ نوافذ الملفّ.
   *
   * ومفاتيحُ الاستعلام هي نفسُها التي تستعملها شاشاتُ الإعداد، فلا تُجلب
   * مرّتين ولا تفترق: من يضيف وردّيةً في «الشركة» يجدها هنا في اللحظة
   * نفسِها بلا إعادة تحميل.
   */
  const units = useQuery({
    queryKey: ['hr', 'setup', 'units', 'options'],
    queryFn: () => api<UnitOption[]>('/hr/setup/units/options'),
  });
  const centers = useQuery({
    queryKey: ['hr', 'setup', 'cost-centers'],
    queryFn: () => api<CenterOption[]>('/hr/setup/cost-centers'),
  });
  const plans = useQuery({
    queryKey: ['hr', 'setup', 'schedules'],
    queryFn: () => api<PlanOption[]>('/hr/setup/schedules'),
  });
  const positions = useQuery({
    queryKey: ['hr', 'org', 'positions'],
    queryFn: () => api<PositionOption[]>('/hr/org/positions'),
  });
  const grades = useQuery({
    queryKey: ['hr', 'org', 'grades'],
    queryFn: () => api<GradeOption[]>('/hr/org/grades'),
  });
  const managers = useQuery({
    queryKey: ['employees', { pageSize: 200 }],
    queryFn: () =>
      api<{ items: { id: string; name: string }[] }>('/hr/employees?pageSize=200'),
  });

  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const save = useMutation({
    mutationFn: () =>
      api(editing ? `/hr/employees/${employee!.id}` : '/hr/employees', {
        method: editing ? 'PATCH' : 'POST',
        body: {
          name: form.name,
          code: form.code || null,
          jobTitle: form.jobTitle || null,
          deptKey: form.deptKey || null,
          email: form.email || null,
          phone: form.phone || null,
          hireDate: form.hireDate,
          baseSalary: Number(form.baseSalary) || 0,
          status: form.status,
          annualLeaveBalance: Number(form.annualLeaveBalance) || 0,
          insured: form.insured,
          insuranceNumber: form.insuranceNumber || null,
          insuranceWage: form.insuranceWage
            ? Number(form.insuranceWage)
            : null,
          taxable: form.taxable,
          orgUnitId: form.orgUnitId || null,
          costCenterId: form.costCenterId || null,
          scheduleId: form.scheduleId || null,
          positionId: form.positionId || null,
          gradeId: form.gradeId || null,
          workMode: form.workMode,
          managerId: form.managerId || null,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] });
      onClose();
    },
  });

  const valid = form.name.trim().length >= 2 && form.hireDate.length === 10;

  return (
    <Modal
      title={editing ? `تعديل ${employee!.name}` : 'موظف جديد'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field span-2">
          <label className="label req" htmlFor="e-name">
            الاسم
          </label>
          <input
            id="e-name"
            className="input"
            value={form.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="e-code">
            الكود الوظيفي
          </label>
          <input
            id="e-code"
            className="input num"
            dir="ltr"
            value={form.code}
            onChange={(e) => set({ code: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="e-title">
            المسمّى الوظيفي
          </label>
          <input
            id="e-title"
            className="input"
            value={form.jobTitle}
            onChange={(e) => set({ jobTitle: e.target.value })}
          />
        </div>

        {/*
          القسمُ اختيارٌ لا كتابةٌ حرّة — وهو الذي يقرّر أين يظهر الموظّف.

          كان نصًّا حرًّا فلم يكن يعني للنظام شيئًا: «مبيعات» و«المبيعات»
          و«قسم المبيعات» ثلاثةُ أقسام، وكلُّ موظّفٍ يظهر في كلّ فريق.
          والاختيارُ من الكتالوج يجعل السؤالَ يُسأل مرّةً ويسري في النظام.
        */}
        <div className="field">
          <label className="label" htmlFor="e-dept">
            القسم
          </label>
          <select
            id="e-dept"
            className="input"
            value={form.deptKey}
            onChange={(e) => set({ deptKey: e.target.value })}
          >
            <option value="">— اختر القسم —</option>
            {DEPARTMENT_DEFS.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {form.deptKey
              ? DEPARTMENT_MAP[form.deptKey]?.hint
              : 'بلا قسمٍ لا يظهر الموظّف في أيّ فريقٍ ولا تُسنَد إليه مهامّ.'}
          </span>
        </div>

        <div className="field">
          <label className="label req" htmlFor="e-hire">
            تاريخ التعيين
          </label>
          <input
            id="e-hire"
            className="input num"
            type="date"
            value={form.hireDate}
            onChange={(e) => set({ hireDate: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="e-email">
            البريد
          </label>
          <input
            id="e-email"
            className="input num"
            dir="ltr"
            type="email"
            value={form.email}
            onChange={(e) => set({ email: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="e-phone">
            الهاتف
          </label>
          <input
            id="e-phone"
            className="input num"
            dir="ltr"
            value={form.phone}
            onChange={(e) => set({ phone: e.target.value })}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="e-salary">
            الراتب الأساسي
          </label>
          <input
            id="e-salary"
            className="input num"
            dir="ltr"
            inputMode="decimal"
            value={form.baseSalary}
            onChange={(e) => set({ baseSalary: e.target.value })}
          />
          <span className="tiny mute">
            أساس القسيمة: أجر اليوم = الأساسي ÷ ٣٠، وعليه يُحسب الغياب والعمل الإضافي.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-leave">
            رصيد الإجازة السنوية
          </label>
          <input
            id="e-leave"
            className="input num"
            dir="ltr"
            inputMode="numeric"
            value={form.annualLeaveBalance}
            onChange={(e) => set({ annualLeaveBalance: e.target.value })}
          />
        </div>

        {/*
          التأمينُ والضريبةُ لكلّ موظّفٍ على حدة.

          متدرّبٌ أو متعاقدٌ بالقطعة لا يُؤمَّن عليه، وتفعيلُهما للجميع
          يخصم من راتب من لا تأمينَ له ولا ضريبةَ عليه. والنِّسبُ
          والشرائحُ في «التأمينات والضريبة» بالإعدادات.
        */}
        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={form.insured}
              onChange={(e) => set({ insured: e.target.checked })}
            />
            مؤمَّنٌ عليه اجتماعيًّا
          </label>
        </div>

        <div className="field">
          <label className="row tiny" style={{ gap: 6 }}>
            <input
              type="checkbox"
              checked={form.taxable}
              onChange={(e) => set({ taxable: e.target.checked })}
            />
            تُخصم منه ضريبة الدخل
          </label>
        </div>

        {form.insured && (
          <>
            <div className="field">
              <label className="label" htmlFor="e-ins-no">
                الرقم التأميني
              </label>
              <input
                id="e-ins-no"
                className="input num"
                dir="ltr"
                value={form.insuranceNumber}
                onChange={(e) => set({ insuranceNumber: e.target.value })}
              />
            </div>

            <div className="field">
              <label className="label" htmlFor="e-ins-wage">
                الأجر التأميني
              </label>
              <input
                id="e-ins-wage"
                className="input num"
                dir="ltr"
                inputMode="numeric"
                value={form.insuranceWage}
                onChange={(e) => set({ insuranceWage: e.target.value })}
              />
              <span className="tiny mute">
                اتركه فارغًا ليُؤمَّن على الأساسيّ — ويُحدّ بالحدّين في
                الإعدادات.
              </span>
            </div>
          </>
        )}

        {/* ---------- ما يُختار من إعداد المؤسسة ---------- */}

        <div className="field span-2">
          <h3 className="label" style={{ marginBottom: 0 }}>
            النظام الذي يعمل تحته
          </h3>
          {/*
            كلُّها تُختار ولا تُكتب: عُرِّفت مرّةً في «الشركة» و«الهيكل
            التنظيمي» و«الأوصاف الوظيفية»، وتُختار هنا فتصير هي النظامَ
            المطبَّق على هذا الموظّف — يُقاس به حضورُه وتُحمَّل به كلفتُه.
          */}
          <span className="tiny mute">
            تُعرَّف مرّةً في «الشركة» و«الهيكل التنظيمي»، وتُختار هنا. وما
            يُختار يصير النظامَ المطبَّق عليه: تُقاس ساعاتُه على خطّته،
            وتُحمَّل كلفتُه على مركزه، ويظهر في وحدته في الهيكل.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-unit">
            الوحدة التنظيمية
          </label>
          <select
            id="e-unit"
            className="input"
            value={form.orgUnitId}
            onChange={(e) => set({ orgUnitId: e.target.value })}
          >
            <option value="">بلا وحدة</option>
            {(units.data ?? []).map((u) => (
              <option key={u.id} value={u.id}>
                {u.path}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            {units.data && units.data.length === 0
              ? 'لا وحداتٍ بعد — تُرسم في «الهيكل التنظيمي».'
              : 'بها يُعدّ في فرعه ويُقرأ عددُ من في كلّ إدارة.'}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-plan">
            خطة العمل
          </label>
          <select
            id="e-plan"
            className="input"
            value={form.scheduleId}
            onChange={(e) => set({ scheduleId: e.target.value })}
          >
            <option value="">ساعات الشركة العامّة</option>
            {(plans.data ?? [])
              .filter((x) => x.isActive)
              .map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name} — {x.startTime} إلى {x.endTime}
                </option>
              ))}
          </select>
          {/*
            الخطّةُ مدخلٌ في المرتّب: منها يُقاس التأخيرُ والانصرافُ المبكّر،
            وبلائحتها يُخصم. وتركُها فارغةً يعني ألّا يُخصم منه شيء.
          */}
          <span className="tiny mute">
            منها يُقاس حضورُه وتأخيرُه وعملُه الإضافيّ، وبلائحة جزاءاتها
            يُخصم في كشف المرتّبات. وبلا خطّةٍ يقع على ساعات الشركة العامّة
            ولا تُطبَّق عليه لائحةُ جزاءات.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-mode">
            نمط العمل
          </label>
          <select
            id="e-mode"
            className="input"
            value={form.workMode}
            onChange={(e) => set({ workMode: e.target.value })}
          >
            {WORK_MODES.map((m) => (
              <option key={m} value={m}>
                {WORK_MODE_MAP[m].label}
              </option>
            ))}
          </select>
          {/* «دوام كامل» نوعُ تعاقدٍ و«عن بُعد» مكانُ عمل — ومن يعمل كاملًا من بيته يحتاج الاثنين. */}
          <span className="tiny mute">
            {WORK_MODE_MAP[form.workMode as WorkMode]?.description ?? ''}
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-cc">
            مركز التكلفة
          </label>
          <select
            id="e-cc"
            className="input"
            value={form.costCenterId}
            onChange={(e) => set({ costCenterId: e.target.value })}
          >
            <option value="">بلا مركز</option>
            {(centers.data ?? [])
              .filter((c) => c.isActive)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.name}
                </option>
              ))}
          </select>
          <span className="tiny mute">عليه يُحمَّل راتبُه في تقارير الكلفة.</span>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-pos">
            الوصف الوظيفي المعتمَد
          </label>
          <select
            id="e-pos"
            className="input"
            value={form.positionId}
            onChange={(e) => set({ positionId: e.target.value })}
          >
            <option value="">بلا ربط</option>
            {(positions.data ?? []).map((x) => (
              <option key={x.id} value={x.id}>
                {x.title}
                {x.department ? ` — ${x.department}` : ''}
              </option>
            ))}
          </select>
          {/*
            الربطُ هو ما يجعل «المعتمَد والمشغول» في تخطيط القوى العاملة
            رقمًا: بلا مفتاحٍ يُعدّ الموظّفُ خارجَ كلّ وصفٍ معتمَد.
          */}
          <span className="tiny mute">
            به يُعدّ في «المشغول» أمام المعتمَد في تخطيط القوى العاملة.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-grade">
            الدرجة
          </label>
          <select
            id="e-grade"
            className="input"
            value={form.gradeId}
            onChange={(e) => set({ gradeId: e.target.value })}
          >
            <option value="">بلا درجة</option>
            {(grades.data ?? []).map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
          <span className="tiny mute">
            بها تُقارن نسبةُ راتبه إلى وسط درجته.
          </span>
        </div>

        <div className="field">
          <label className="label" htmlFor="e-manager">
            مديره المباشر
          </label>
          <select
            id="e-manager"
            className="input"
            value={form.managerId}
            onChange={(e) => set({ managerId: e.target.value })}
          >
            <option value="">بلا مدير</option>
            {(managers.data?.items ?? [])
              .filter((m) => m.id !== employee?.id)
              .map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
          </select>
          <span className="tiny mute">
            إليه تصل طلباتُ إجازته وعليه يظهر في شاشته.
          </span>
        </div>

        {/*
          وسمُ «مندوب مبيعات» صار يُشتقّ من القسم لا يُسأل عنه مرّتين.

          كان مربّعًا يُنسى، فيُعيَّن الرجلُ في المبيعات ولا يظهر في فريقها
          ولا يدخل كشفَ عمولة، ويشكو صاحبُ النظام أنّ الشاشة لا تعمل.
        */}
        <div className="field span-2">
          <p className="tiny mute">
            {form.deptKey
              ? `الموضع في النظام: ${DEPARTMENT_MAP[form.deptKey]?.hint ?? '—'}`
              : 'اختر القسم أعلاه ليُعرف موضعُ الموظّف في النظام.'}
          </p>
        </div>
      </div>
    </Modal>
  );
}
